/**
 * Headline Service
 *
 * Centralized service for building and rendering headline content.
 * Eliminates duplicate legacyData construction and headline rendering logic
 * across index.js.
 *
 * Usage:
 *   import { HeadlineService } from './services/HeadlineService.js';
 *
 *   const legacyData = HeadlineService.buildLegacyData(context);
 *   HeadlineService.renderPortfolioHeadline(elements, config, legacyData, calculatorResult);
 *   HeadlineService.renderClassHeadline(elements, config, legacyData, classKey, classData);
 */

import { HeadlineFormatter } from '../formatters/HeadlineFormatter.js';

const HeadlineService = {

  /**
   * Parse a money value from a formatted string like "$29,987.25"
   * @param {string} str - String containing money value
   * @returns {number|null} - Parsed number or null if not found
   */
  _parseMoney: function(str) {
    if (!str || typeof str !== 'string') return null;
    // Remove $ and commas, then parse
    const cleaned = str.replace(/[$,]/g, '');
    const num = parseFloat(cleaned);
    return isNaN(num) ? null : num;
  },

  /**
   * Extract grant values from a conditions string
   * Parses strings like: "Stock Price @ Grant: $123.83<br>Original Grant Value: $29,987.25<br>Forecasted Value: $34,107.21"
   *
   * @param {string} conditions - The conditions string from vesting schedule
   * @returns {Object} - { stockPriceAtGrant, originalGrantValue, forecastedValue, actualGrantValue }
   */
  _parseGrantConditions: function(conditions) {
    const result = {
      stockPriceAtGrant: null,
      originalGrantValue: null,
      forecastedValue: null,
      actualGrantValue: null
    };

    if (!conditions || typeof conditions !== 'string') return result;

    // Stock Price @ Grant: $X.XX
    const stockPriceMatch = conditions.match(/Stock Price @ Grant:\s*(\$[\d,]+\.?\d*)/i);
    if (stockPriceMatch) {
      result.stockPriceAtGrant = this._parseMoney(stockPriceMatch[1]);
    }

    // Original Grant Value: $X,XXX.XX
    const originalMatch = conditions.match(/Original Grant Value:\s*(\$[\d,]+\.?\d*)/i);
    if (originalMatch) {
      result.originalGrantValue = this._parseMoney(originalMatch[1]);
    }

    // Forecasted Value: $X,XXX.XX
    const forecastMatch = conditions.match(/Forecasted Value:\s*(\$[\d,]+\.?\d*)/i);
    if (forecastMatch) {
      result.forecastedValue = this._parseMoney(forecastMatch[1]);
    }

    // Actual Grant Value: $X,XXX.XX (for vested grants)
    const actualMatch = conditions.match(/Actual Grant Value:\s*(\$[\d,]+\.?\d*)/i);
    if (actualMatch) {
      result.actualGrantValue = this._parseMoney(actualMatch[1]);
    }

    return result;
  },

  /**
   * Aggregate grant values from vesting schedules across all share classes
   * Parses conditions strings to extract Original Grant Value and Forecasted Value
   *
   * @param {Object} userData - User data object
   * @param {string} client - Client code
   * @param {string[]} shareKeys - Array of share class keys
   * @returns {Object} - { totalOriginalGrantValue, totalForecastedValue, totalActualGrantValue }
   */
  aggregateGrantValues: function(userData, client, shareKeys) {
    const result = {
      totalOriginalGrantValue: 0,
      totalForecastedValue: 0,
      totalActualGrantValue: 0
    };

    if (!userData || !client || !shareKeys) return result;

    const userClientData = userData.clients?.[client];
    if (!userClientData?.shares) return result;

    shareKeys.forEach(key => {
      const vesting = userClientData.shares[key]?.vesting;
      if (!vesting || typeof vesting !== 'object') return;

      Object.values(vesting).forEach(grant => {
        if (!grant || typeof grant !== 'object') return;

        const parsed = this._parseGrantConditions(grant.conditions);

        // For unvested grants: accumulate original and forecasted values
        if (!grant.vested) {
          if (parsed.originalGrantValue) {
            result.totalOriginalGrantValue += parsed.originalGrantValue;
          }
          if (parsed.forecastedValue) {
            result.totalForecastedValue += parsed.forecastedValue;
          }
        } else {
          // For vested grants: accumulate actual values
          if (parsed.actualGrantValue) {
            result.totalActualGrantValue += parsed.actualGrantValue;
          }
        }
      });
    });

    return result;
  },

  /**
   * Build the legacy data object used by HeadlineFormatter
   * This consolidates the duplicate legacyData construction across index.js
   *
   * @param {Object} context - Data context from getEquityData
   * @param {number} context.totalPaidInCapital - Total paid-in capital
   * @param {number} context.totalCurrentValue - Total current value
   * @param {number} context.totalDistributions - Total distributions
   * @param {Object} context.bases - Cost basis by class
   * @param {Object} context.latestValue - Latest value by class
   * @param {Object} context.distribs - Distributions by class
   * @param {Object} context.contributions - Contributions by class
   * @param {Object} context.returnsOfCapital - Returns of capital by class
   * @param {string[]} context.shareKeys - Array of share class keys
   * @param {string} context.labelPaidIn - Label for paid-in capital
   * @param {boolean} context.itsaFund - Whether this is a fund (UFL flag)
   * @param {string} context.valuationDate - Formatted valuation date string
   * @param {string} [context.currentClassValuationDate] - Formatted class valuation date
   * @param {string} [context.stockStatus] - User's stock status for eligibility (e.g., "10", "100", "Associate")
   * @param {number} [context.totalOwned] - Total shares owned across all classes
   * @param {number} [context.totalCommitted] - Total committed shares across all classes
   * @param {Object} [context.userData] - Full user data object (for path-based variables)
   * @param {Object} [context.clientData] - Full client data object (for path-based variables)
   * @param {string} [context.client] - Client code (for path-based variable resolution)
   * @returns {Object} - Legacy data object for HeadlineFormatter
   */
  buildLegacyData: function(context) {
    const {
      totalPaidInCapital = 0,
      totalCurrentValue = 0,
      totalDistributions = 0,
      totalCapitalAccount = 0,
      bases = {},
      latestValue = {},
      distribs = {},
      contributions = {},
      returnsOfCapital = {},
      shareKeys = [],
      labelPaidIn = 'Paid-In Capital',
      itsaFund = false,
      valuationDate = '',
      currentClassValuationDate = '',
      stockStatus = null,
      totalOwned = 0,
      totalCommitted = 0,
      userData = null,
      clientData = null,
      client = ''
    } = context;

    const result = {
      totalPaidInCapital,
      totalCurrentValue,
      totalDistributions,
      totalCapitalAccount,
      netValue: totalCurrentValue - totalPaidInCapital,
      bases,
      latestValue,
      distribs,
      contributions,
      returnsOfCapital,
      shareKeys,
      labelPaidIn,
      itsaFund,
      valuationDate,
      currentClassValuationDate
    };

    // Add stock status eligibility data if provided
    if (stockStatus) {
      result.stockStatus = stockStatus;
      result.totalOwned = totalOwned;
      result.totalCommitted = totalCommitted;
    }

    // Add context for path-based variable resolution
    if (userData) result.userData = userData;
    if (clientData) result.clientData = clientData;
    if (client) result.client = client;

    // Aggregate grant values from vesting schedules (BW-style)
    // Parses conditions strings to extract Original Grant Value and Forecasted Value
    if (userData && client && shareKeys.length) {
      const grantValues = this.aggregateGrantValues(userData, client, shareKeys);
      if (grantValues.totalOriginalGrantValue > 0) {
        result.totalOriginalGrantValue = grantValues.totalOriginalGrantValue;
      }
      if (grantValues.totalForecastedValue > 0) {
        result.totalForecastedValue = grantValues.totalForecastedValue;
      }
      if (grantValues.totalActualGrantValue > 0) {
        result.totalActualGrantValue = grantValues.totalActualGrantValue;
      }
    }

    return result;
  },

  /**
   * Render a portfolio-level headline to DOM elements
   *
   * @param {Object} elements - DOM elements to update
   * @param {HTMLElement} elements.label - Main headline label element
   * @param {HTMLElement} elements.amount - Amount element (cleared for template headlines)
   * @param {HTMLElement} elements.subtitle - Subtitle/date element
   * @param {HTMLElement} [elements.tertiary] - Tertiary/third line element
   * @param {Object} config - Template configuration
   * @param {string} config.template - Headline template
   * @param {string} [config.subtitleTemplate] - Subtitle template
   * @param {string} [config.tertiaryTemplate] - Tertiary line template
   * @param {Object} legacyData - Legacy data from buildLegacyData
   * @param {Object} [calculatorResult] - Calculator result if available
   * @returns {Object} - { handled: boolean, headline: { main, subtitle, tertiary } }
   */
  renderPortfolioHeadline: function(elements, config, legacyData, calculatorResult) {
    const { label, amount, subtitle, tertiary } = elements;

    if (!label) {
      return { handled: false, headline: null };
    }

    const headlineConfig = {
      template: config.template,
      subtitleTemplate: config.subtitleTemplate || '',
      tertiaryTemplate: config.tertiaryTemplate || ''
    };

    const headline = HeadlineFormatter.buildUnifiedHeadline(headlineConfig, {
      calculatorResult: calculatorResult,
      legacyData: legacyData
    });

    // Check if headline has meaningful value
    const calcTotalValue = calculatorResult?.totalValue || 0;
    const calcVestedValue = calculatorResult?.vestedValue || 0;
    const calcUnvestedValue = calculatorResult?.unvestedValue || 0;
    const hasMeaningfulValue = legacyData.totalPaidInCapital > 0 ||
                               legacyData.totalCurrentValue > 0 ||
                               calcTotalValue > 0 ||
                               calcVestedValue > 0 ||
                               calcUnvestedValue > 0;

    if (headline.main && hasMeaningfulValue) {
      label.innerHTML = headline.main;
      if (amount) amount.innerHTML = '';
      if (subtitle) {
        subtitle.innerHTML = headline.subtitle
          ? `<div class="text-base font-normal mt-1 mb-1 text-secondary">${headline.subtitle}</div>`
          : '';
      }
      if (tertiary) {
        tertiary.innerHTML = headline.tertiary
          ? `<div class="text-base font-normal mt-1 mb-1 text-secondary">${headline.tertiary}</div>`
          : '';
      }
      return { handled: true, headline };
    }

    return { handled: false, headline };
  },

  /**
   * Render a class-level headline to DOM elements
   *
   * @param {Object} elements - DOM elements to update
   * @param {HTMLElement} elements.label - Main headline label element
   * @param {HTMLElement} elements.amount - Amount element (cleared for template headlines)
   * @param {HTMLElement} elements.subtitle - Subtitle/date element
   * @param {HTMLElement} [elements.tertiary] - Tertiary/third line element
   * @param {Object} config - Template configuration
   * @param {string} config.classTemplate - Class headline template
   * @param {string} [config.classSubtitleTemplate] - Class subtitle template
   * @param {string} [config.classTertiaryTemplate] - Class tertiary template
   * @param {Object} legacyData - Legacy data from buildLegacyData
   * @param {string} classKey - Current share class key
   * @param {Object} [classData] - Class data from calculator breakdown
   * @param {Object} [equityConfig] - Equity configuration for this class
   * @returns {Object} - { handled: boolean, headline: { main, subtitle, tertiary } }
   */
  renderClassHeadline: function(elements, config, legacyData, classKey, classData, equityConfig) {
    const { label, amount, subtitle, tertiary } = elements;

    if (!label) {
      return { handled: false, headline: null };
    }

    const headlineConfig = {
      classTemplate: config.classTemplate,
      classSubtitleTemplate: config.classSubtitleTemplate || '',
      classTertiaryTemplate: config.classTertiaryTemplate || ''
    };

    const equityName = equityConfig?.name || classKey;
    const currentLabel = equityConfig?.currentLabel || '';

    const headline = HeadlineFormatter.buildUnifiedClassHeadline(headlineConfig, {
      classData: classData,
      legacyData: legacyData,
      classKey: classKey,
      equityName: equityName,
      currentLabel: currentLabel
    });

    // Check if headline has meaningful value
    const classValue = classData?.value || legacyData.latestValue?.[classKey] || 0;
    const classBasis = legacyData.bases?.[classKey] || 0;
    const vestedValue = classData?.vestedValue || 0;
    const unvestedValue = classData?.unvestedValue || 0;

    if (headline.main && (classValue > 0 || classBasis > 0 || vestedValue > 0 || unvestedValue > 0)) {
      label.innerHTML = headline.main;
      if (amount) amount.innerHTML = '';
      if (subtitle) {
        subtitle.innerHTML = headline.subtitle
          ? `<div class="text-base font-normal mt-1 mb-1 text-secondary">${headline.subtitle}</div>`
          : '';
      }
      if (tertiary) {
        tertiary.innerHTML = headline.tertiary
          ? `<div class="text-base font-normal mt-1 mb-1 text-secondary">${headline.tertiary}</div>`
          : '';
      }
      return { handled: true, headline };
    }

    return { handled: false, headline };
  },

  /**
   * Render headline to graph view elements (tradingData section)
   * Used when graph is displayed instead of landing page
   *
   * @param {Object} headline - { main, subtitle, tertiary } from HeadlineFormatter
   */
  renderToGraphElements: function(headline) {
    const currentLabel = document.getElementById('currentLabel');
    const currentSymbol = document.getElementById('currentSymbol');
    const currentValue = document.getElementById('currentValue');
    const priceLabel = document.getElementById('priceLabel');
    const lastTraded = document.getElementById('lastTraded');
    const priceLabelReport = document.getElementById('priceLabelReport');
    const tertiaryLine = document.getElementById('headlineTertiary');

    if (currentLabel && currentSymbol && currentValue) {
      currentLabel.innerHTML = '';
      currentSymbol.innerHTML = headline.main;
      currentValue.innerHTML = '';
    }

    // Always update subtitle elements when template system is handling the headline
    // Hide the "Most recent Price" line if no subtitle, show subtitle if present
    if (priceLabel && lastTraded) {
      if (headline.subtitle) {
        priceLabel.innerHTML = '';
        lastTraded.innerHTML = headline.subtitle;
        if (priceLabelReport) priceLabelReport.classList.remove('hidden');
      } else {
        // Hide the price label line when there's no subtitle
        if (priceLabelReport) priceLabelReport.classList.add('hidden');
      }
    }

    // Render tertiary line if element exists and content is present
    if (tertiaryLine) {
      tertiaryLine.innerHTML = headline.tertiary || '';
      tertiaryLine.classList.toggle('hidden', !headline.tertiary);
    }
  }
};

export { HeadlineService };
